import React, { useEffect, useState } from "react";
import { 
  Box, 
  CircularProgress, 
  Typography, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel, 
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Line } from "react-chartjs-2";
import axiosInstance from "utils/axiosInstance";
import moment from "moment";
import apiConfig from "config/appConfig";

const RequestsOverTime = () => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [interval, setInterval] = useState("hour");
  const [error, setError] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    const fetchLogs = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosInstance.get(`${apiConfig.endpoints.dashboard.requestsOverTime}?interval=${interval}&days=7`);
        const timeData = response.data;

        if (!timeData.labels.length) {
          throw new Error("No data available for the selected interval.");
        }

        setChartData({
          labels: timeData.labels,
          datasets: [
            {
              label: `Requests Per ${interval.charAt(0).toUpperCase() + interval.slice(1)}`,
              data: timeData.data.map(item => item.total),
              backgroundColor: "rgba(33, 150, 243, 0.5)",
              borderColor: "#2196f3",
              borderWidth: 2,
              tension: 0.2,
              fill: true,
            },
          ],
        });
      } catch (err) {
        console.error("Error fetching requests over time:", err);
        setError(err.message || "Failed to fetch requests over time data.");
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [interval]);

  const groupByInterval = (logs, interval) => {
    const formatMap = {
      minute: "YYYY-MM-DD HH:mm",
      hour: "YYYY-MM-DD HH",
      day: "YYYY-MM-DD",
    };
    const format = formatMap[interval];

    // Group and count logs
    const grouped = logs.reduce((acc, log) => {
      const timeKey = moment(log.executedAt).format(format);
      acc[timeKey] = (acc[timeKey] || 0) + 1;
      return acc;
    }, {});

    // Create sorted labels and corresponding data
    const labels = Object.keys(grouped).sort();
    const data = labels.map((label) => grouped[label]);

    return { labels, data };
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: !isMobile,
        position: 'top',
        labels: {
          font: {
            size: isMobile ? 10 : 12,
          },
        },
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        titleFont: {
          size: isMobile ? 10 : 12,
        },
        bodyFont: {
          size: isMobile ? 10 : 12,
        },
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: !isMobile,
          text: 'Time',
          font: {
            size: isMobile ? 10 : 12,
          },
        },
        ticks: {
          font: {
            size: isMobile ? 8 : 10,
          },
          maxRotation: isMobile ? 45 : 0,
        },
      },
      y: {
        display: true,
        title: {
          display: !isMobile,
          text: 'Requests',
          font: {
            size: isMobile ? 10 : 12,
          },
        },
        ticks: {
          font: {
            size: isMobile ? 8 : 10,
          },
        },
      },
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false,
    },
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: { xs: 250, sm: 300 },
          minHeight: { xs: 250, sm: 300 },
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: { xs: 2, sm: 3 } }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!chartData) {
    return (
      <Box sx={{ 
        padding: { xs: 2, sm: 3 },
        textAlign: "center",
        height: { xs: 250, sm: 300 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}>
        <Typography variant="body1">No data available</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: "100%", 
      height: "100%",
      display: "flex",
      flexDirection: "column",
    }}>
      <Typography 
        variant={isMobile ? "h6" : "h5"} 
        align="center" 
        gutterBottom
        sx={{ 
          fontSize: { xs: '1rem', sm: '1.25rem' },
          fontWeight: 600,
          mb: 1
        }}
      >
        Requests Over Time
      </Typography>
      
      <FormControl 
        fullWidth 
        margin="normal"
        size={isMobile ? "small" : "medium"}
      >
        <InputLabel id="interval-select-label">Group By</InputLabel>
        <Select
          labelId="interval-select-label"
          value={interval}
          onChange={(e) => setInterval(e.target.value)}
          label="Group By"
        >
          <MenuItem value="minute">Per Minute</MenuItem>
          <MenuItem value="hour">Per Hour</MenuItem>
          <MenuItem value="day">Per Day</MenuItem>
        </Select>
      </FormControl>
      
      <Box sx={{ 
        flexGrow: 1,
        position: "relative",
        minHeight: 300,
      }}>
        <Line data={chartData} options={chartOptions} />
      </Box>
    </Box>
  );
};

export default RequestsOverTime;
